import { useEffect } from 'react'
import { z } from 'zod'
import { reportError } from '@mc/monitoring'
import { usePromotion } from './usePromotion'

const StyledFeatureVariableSchema = z.object({
  text: z.union([
    z.string(),
    z.array(
      z.object({
        style: z.string(),
        copy: z.string(),
      }),
    ),
  ]),
})

const UrgencyFeatureVariableSchema = z.record(
  z.string(),
  z.object({
    available_from: z.coerce.date(),
    text_primary_orange: z.string(),
    text_and_offer_name_green: z.string(),
  }),
)

const TermsFeatureVariablesSchema = z.record(
  z.string(),
  z.object({
    available_from: z.coerce.date(),
    text: z.string(),
    link: z.string().url(),
  }),
)

const PromoFeatureVariablesSchema = z.object({
  promo_key: z.string(),

  // Promo mechanic
  gift_eligible: z.boolean(),
  coupon_codes: z.object({
    standard: z.string().optional(),
    plus: z.string().optional(),
    premium: z.string().optional(),
  }),
  percentages: z.object({
    standard: z.number().optional(),
    plus: z.number().optional(),
    premium: z.number().optional(),
  }),

  // Images
  promo_page_hero_image: z.string().url(),
  gift_page_hero_image: z.string().url(),
  home_page_hero_images: z.array(
    z.object({
      firstColumn: z.string().url(),
      secondColumn: z.string().url(),
    }),
  ),

  // pen-5453_v1_hp_carousel_to_quiz
  home_page_hero_images_pen_5453_v1: z.array(
    z.object({
      slug: z.string(),
      carouselImageURL: z.string().url(),
      itemType: z.enum(['subcategory']).optional(),
    }),
  ),

  // Copies
  quiz_sticky_footer_copy: StyledFeatureVariableSchema,
  discount_modal_promo_overriden_body: StyledFeatureVariableSchema,
  discount_modal_promo_overriden_cta: StyledFeatureVariableSchema,
  banner_offer_slogan_blue: StyledFeatureVariableSchema,
  offer_slogan_yellow: StyledFeatureVariableSchema,
  modal_offer_slogan: StyledFeatureVariableSchema,
  gift_page_title: StyledFeatureVariableSchema.optional(),
  gift_page_subtitle: StyledFeatureVariableSchema.optional(),

  // Urgency
  offer_terms_teal: TermsFeatureVariablesSchema,
  urgency_texts_orange_green: UrgencyFeatureVariableSchema,
})

export const useParseFeatureVariables = () => {
  const { isPromotionOn, variables } = usePromotion()

  useEffect(() => {
    if (isPromotionOn) {
      parseFeatureVariables(variables)
    }
  }, [isPromotionOn, variables])
}

export const parseFeatureVariables = (variables: Record<string, unknown>) => {
  if (!variables) {
    reportError(
      'Error getting experiment variables while trying to validate tiers discount',
    )
    return null
  }

  const result = PromoFeatureVariablesSchema.safeParse(variables) || {}

  if (result.error) {
    // eslint-disable-next-line no-console
    console.error('Invalid promo variables', result.error?.format())
    reportError('Invalid promo variables', {
      err: String(result.error?.format()),
    })
    return null
  }

  return result.data
}
